import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WITHOUT_NOTIFICATION_HEADER } from '@core/constants';
import { copyToClipboard } from '@core/functions';
import {
  CollectionNoticeModel,
  ExternalCollectionNoticeModel,
  ExternalPaymentParamsModel,
  PaginationModel,
  SingleItemResponse,
  WithoutNotificationHeader,
} from '@core/models';
import { NotificationService } from '@core/services';
import { Api } from '@core/services/api.state';
import { ApiService } from '@core/services/api/api.service';
import { environment } from '@env/environment';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ExternalCollectionsService extends Api<ExternalCollectionNoticeModel> {
  override endpoint: string;

  constructor(
    protected override readonly apiService: ApiService<ExternalCollectionNoticeModel>,
    private http: HttpClient,
    private notificationService: NotificationService
  ) {
    super(apiService);
    this.endpoint = `${environment.apiUrl}/v1/admin/collections/collection-notices`;
  }

  public copyLink(collectionNoticeId: string): Observable<string> {
    const endpoint = `${this.endpoint}/external/${collectionNoticeId}/link`;
    return this.http.get<SingleItemResponse<{ link: string }>>(endpoint).pipe(
      map((response) => {
        copyToClipboard(response.data.link);
        this.notificationService.displayAlert(
          'El enlace de pago se ha copiado al portapapeles.',
          'success'
        );
        return response.data.link;
      })
    );
  }

  public create(
    data: Partial<ExternalCollectionNoticeModel>
  ): Observable<SingleItemResponse<ExternalCollectionNoticeModel>> {
    const endpoint = `${this.endpoint}/create-external`;
    return this.http.post<SingleItemResponse<ExternalCollectionNoticeModel>>(
      endpoint,
      data
    );
  }

  public export(params: PaginationModel): Observable<Blob> {
    params.limit = 1000000;
    params['only_external'] = 1;
    const endpoint = `${this.endpoint}/export/excel?${new URLSearchParams(
      params as any
    ).toString()}`;

    return this.http.get(endpoint, {
      responseType: 'blob',
    });
  }

  public getExternalCollectionNotice(
    params: Partial<ExternalPaymentParamsModel>,
    withoutNotification: WithoutNotificationHeader = WithoutNotificationHeader.SUCCESS
  ): Observable<CollectionNoticeModel> {
    const endpoint = `${environment.apiUrl}/v1/external/collection-notices/${params.collectionNoticeId}`;
    return this.http
      .get<SingleItemResponse<CollectionNoticeModel>>(endpoint, {
        headers: {
          [WITHOUT_NOTIFICATION_HEADER]: withoutNotification,
        },
      })
      .pipe(
        map((result) => {
          return result.data;
        })
      );
  }

  public resendEmail(
    collectionNoticeId: string
  ): Observable<SingleItemResponse<ExternalCollectionNoticeModel>> {
    const endpoint = `${this.endpoint}/${collectionNoticeId}/external/resend-email`;
    return this.http.post<SingleItemResponse<ExternalCollectionNoticeModel>>(
      endpoint,
      null
    );
  }

  public updateEmail(
    collectionNoticeId: string,
    email: string
  ): Observable<SingleItemResponse<ExternalCollectionNoticeModel>> {
    const endpoint = `${this.endpoint}/${collectionNoticeId}/external/change-email`;
    return this.http.put<SingleItemResponse<ExternalCollectionNoticeModel>>(
      endpoint,
      { email }
    );
  }
}
